require("./account-threepl-services.scss");

import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, signal} from "@angular/core";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {filter} from "rxjs";
import {ThreePLServicesStore} from "../../state/threepl-services.store";
import {ThreePLServiceDetails} from "../../models/threepl-service-details.model";
import {
  ThreePLServicesListQuery, ThreePLServicesSortName, ThreePLServicesSortValue, ThreePLServicesTableLayout, nextSortValue,
} from "../../models/threepl-services-list.model";
import {ThreePLServiceActionsService} from "../../services/threepl-service-actions.service";
import {ThreePLServicesChannelService} from "../../services/threepl-services-channel.service";
import {ServiceActionEvent} from "../threepl-service-actions/threepl-service-actions.component";
import {BringozDateRange} from "../../../../commons/angular/models/bringoz-date-range.model";
import {ActionReasonEntityEnum} from "../../../../action-reasons/enums/action-reason-entity.enum";
import {ThreePLServiceStatusType} from "../../../types/threePL-service-status.type";
import {
  ActionReasonsDialogsServiceService,
} from "../../../../action-reasons/services/action-reasons-dialogs-service.service";
import {CommonsDialogService} from "../../../../commons/services/commons-dialog.service";
import {DateRangeTimeFiltersService} from "../../../../commons/services/date-range-time-filters.service";
import {SplitService} from "../../../../commons/services/split-service.service";
import {FulfillmentDialogService} from "../../../../fulfillment/services/fulfillment-dialogs-service.service";
import {FulfillmentService} from "../../../../fulfillment/services/fulfillment-service.service";
import {ACCOUNT_THREE_PL_SERVICES_PAGE_SIZE} from "../../constants/threepl-services.constant";
import {ILogService} from "angular";

@Component({
  selector: "app-account-threepl-services",
  templateUrl: "./account-threepl-services.html",
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ThreePLServicesStore],
})
export class AccountThreeplServicesComponent implements OnInit {
  readonly account = input.required<{id: string}>();
  readonly isActive = input<boolean>();

  readonly store = inject(ThreePLServicesStore);
  readonly vm = toSignal(this.store.vm$, {requireSync: true});

  private readonly actions = inject(ThreePLServiceActionsService);
  private readonly channel = inject(ThreePLServicesChannelService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dateRangeTimeFiltersService = inject<DateRangeTimeFiltersService>("dateRangeTimeFiltersService" as never);
  private readonly split = inject<SplitService>("splitService" as never);
  private readonly commonsDialogService = inject<CommonsDialogService>("commonsDialogService" as never);
  private readonly actionReasonsDialogsService = inject<ActionReasonsDialogsServiceService>("actionReasonsDialogsService" as never);
  private readonly fulfillmentService = inject<FulfillmentService>("fulfillmentService" as never);
  private readonly fulfillmentDialogService = inject<FulfillmentDialogService>("fulfillmentDialogService" as never);
  private readonly log = inject<ILogService>("$log" as never);

  statusList: string[] = [];
  customTemplates: BringozDateRange[] = [];
  columns: Record<string, boolean> = {};
  layout: Partial<ThreePLServicesTableLayout> = {};

  readonly range = signal<BringozDateRange>({dateStart: undefined, dateEnd: undefined});
  readonly status = signal<string | undefined>(undefined);
  readonly searchTerm = signal<string | undefined>(undefined);
  readonly sortValue = signal<ThreePLServicesSortValue | undefined>(undefined);
  readonly selectedServices = signal<ThreePLServiceDetails[]>([]);
  readonly selectedIds = signal<string[]>([]);

  ngOnInit(): void {
    const isActive = this.isActive();
    const open = [ThreePLServiceStatusType.UNASSIGNED, ThreePLServiceStatusType.ASSIGNED, ThreePLServiceStatusType.STARTED];
    const closed = [ThreePLServiceStatusType.CANCELED, ThreePLServiceStatusType.COMPLETED];
    this.statusList = isActive === undefined ? [...open, ...closed] : isActive ? open : closed;
    this.customTemplates = this.dateRangeTimeFiltersService.getDeliveriesCustomTemplates();
    this.range.set(this.dateRangeTimeFiltersService.today());
    const settings = this.split.getSite3plServiceSettings();
    this.columns = settings?.columns;
    this.layout = {columnSizes: settings?.columnSizes, columnOrder: settings?.columnOrder};

    this.store.load(this.query());

    this.channel.pushes()
      .pipe(filter((push) => push.action !== "created"), takeUntilDestroyed(this.destroyRef))
      .subscribe((push) => this.store.applyPush({action: push.action as "changed" | "partialChanged", entities: push.list}));
  }

  onTableLayoutChange(layout: ThreePLServicesTableLayout): void {
    this.layout = layout;
    this.split.setSite3plServiceSettings({...this.split.getSite3plServiceSettings(), ...layout});
  }

  submitQuery(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    this.requery();
  }

  rangeChanged(range: BringozDateRange | undefined): void {
    if (range && range.name !== this.range().name) {
      this.range.set(range);
      this.requery();
    }
  }

  onStatusChange(status: string | undefined): void {
    this.status.set(status);
    this.requery();
  }

  onSortTypeChange(sortName: ThreePLServicesSortName): void {
    this.sortValue.set(nextSortValue(this.sortValue(), sortName));
    this.requery();
  }

  onToggleService(service: ThreePLServiceDetails): void {
    this.selectedServices.update((selected) => (selected.some((s) => s.id === service.id)
      ? selected.filter((s) => s.id !== service.id)
      : [...selected, service]));
    this.selectedIds.set(this.selectedServices().map((s) => s.id));
  }

  onUnselectAll(): void {
    this.selectedIds.set([]);
    this.selectedServices.set([]);
  }

  onCancelSelectedServices(): void {
    const ids = this.selectedIds();
    Promise.resolve(this.actionReasonsDialogsService.createThreeplServiceCancelReason(ids, undefined, "Cancel Services", true,
      [ActionReasonEntityEnum.Delivery, ActionReasonEntityEnum.Operation]))
      .then((payload) => this.actions.cancelService(ids, payload.cancelReason))
      .catch(() => undefined);
  }

  onAddNoteToSelectedServices(event: MouseEvent): void {
    const ids = this.selectedIds();
    Promise.resolve(this.commonsDialogService.note(event, ids))
      .then((note) => this.actions.addNote(ids, note))
      .catch(() => undefined);
  }

  onMoveToInternal({event, service}: ServiceActionEvent): void {
    const fulfillmentId = service.fulfillmentStepInfoList?.[0]?.fulfillmentId;
    if (!fulfillmentId) {
      return;
    }
    Promise.resolve(this.fulfillmentService.getFulfillment(fulfillmentId, true))
      .then((fulfillment) => this.fulfillmentDialogService.dispatchStep(fulfillment, undefined, event))
      .catch((error: unknown) => {
        if (error) {
          this.log.debug("Failed to dispatch step", error);
        } else {
          this.log.info("Dialog Closed");
        }
      });
  }

  private requery(): void {
    this.selectedIds.set([]);
    this.store.load(this.query());
  }

  private query(): ThreePLServicesListQuery {
    const {dateStart, dateEnd} = this.range();
    return {
      from: dateStart,
      to: dateEnd,
      status: this.status() ? this.status() : this.statusList,
      supplierAccountId: this.account().id,
      searchTerm: this.searchTerm(),
      limit: ACCOUNT_THREE_PL_SERVICES_PAGE_SIZE,
      sort: this.sortValue()?.value,
    };
  }
}
